import { useState, useCallback, useEffect } from 'react';

function readParam(key: string, fallback: string): string {
  return new URLSearchParams(window.location.search).get(key) ?? fallback;
}

function writeParams(updates: Record<string, string | null>) {
  const params = new URLSearchParams(window.location.search);
  for (const [k, v] of Object.entries(updates)) {
    if (v === null || v === '') params.delete(k);
    else params.set(k, v);
  }
  const qs = params.toString();
  const url = qs ? `${window.location.pathname}?${qs}` : window.location.pathname;
  window.history.replaceState(null, '', url);
}

export interface Filters {
  client: string | null;
  search: string;
  searchScope: string | null;   // "alerts" | "clients" | "pipeline" | null=all
  severity: string | null;
  paceStatus: string | null;
  timeWindow: string | null;
  workspace: string | null;
  stage: string | null;
  onlyStale: boolean;
}

/** Preset filter combinations */
export interface SavedView { name: string; filters: Partial<Filters>; tab?: string }

export const SAVED_VIEWS: SavedView[] = [
  { name: 'All reds', filters: { severity: 'red' }, tab: 'alerts' },
  { name: 'Off-track clients', filters: { paceStatus: 'off_track' }, tab: 'pacing' },
  { name: 'Stale only', filters: { onlyStale: true } },
  { name: 'Domain risk', filters: {}, tab: 'domain-health' },
  { name: 'Inbox risk', filters: {}, tab: 'inbox' },
  { name: 'Not measurable', filters: { paceStatus: 'not_measurable' }, tab: 'pacing' },
];

function readFilters(): Filters {
  return {
    client: readParam('client', '') || null,
    search: readParam('q', ''),
    searchScope: readParam('scope', '') || null,
    severity: readParam('sev', '') || null,
    paceStatus: readParam('pace', '') || null,
    timeWindow: readParam('tw', '') || null,
    workspace: readParam('ws', '') || null,
    stage: readParam('stage', '') || null,
    onlyStale: readParam('stale', '') === '1',
  };
}

function writeFilters(f: Filters) {
  writeParams({
    client: f.client, q: f.search || null, scope: f.searchScope,
    sev: f.severity, pace: f.paceStatus, tw: f.timeWindow,
    ws: f.workspace, stage: f.stage, stale: f.onlyStale ? '1' : null,
  });
}

export function useFilters() {
  const [filters, setFiltersState] = useState<Filters>(readFilters);

  useEffect(() => {
    const onPop = () => setFiltersState(readFilters());
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  const setFilter = useCallback(<K extends keyof Filters>(key: K, value: Filters[K]) => {
    setFiltersState(prev => {
      const next = { ...prev, [key]: value };
      writeFilters(next);
      return next;
    });
  }, []);

  const clearFilters = useCallback(() => {
    const cleared: Filters = { client: null, search: '', searchScope: null, severity: null, paceStatus: null, timeWindow: null, workspace: null, stage: null, onlyStale: false };
    writeFilters(cleared);
    setFiltersState(cleared);
  }, []);

  return { filters, setFilter, clearFilters };
}

export function useTabState(defaultTab: string) {
  const [tab, setTabState] = useState(() => readParam('tab', defaultTab));

  useEffect(() => {
    const onPop = () => setTabState(readParam('tab', defaultTab));
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, [defaultTab]);

  const setTab = useCallback((newTab: string) => {
    const params = new URLSearchParams(window.location.search);
    if (newTab === defaultTab) params.delete('tab');
    else params.set('tab', newTab);
    const qs = params.toString();
    const url = qs ? `${window.location.pathname}?${qs}` : window.location.pathname;
    window.history.pushState(null, '', url);
    setTabState(newTab);
  }, [defaultTab]);

  return [tab, setTab] as const;
}
